import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PriceDtoV2, SubscriptionDtoV2, SubscriptionPricingDto } from '@nusign/picflow-backend-types';
import { of, shareReplay, switchMap } from 'rxjs';
import { minTimeBetweenTrialsInDays } from '../consts';
import { GROWTHBOOK } from '../growthbook';
import { PriceDetailsCalculation } from '../interfaces/pricing';
import { AppConfigService } from '../providers/config.service';
import { Product, RecurringInterval } from '../types/product.type';
import { PromotionWithCode } from '../types/promotion';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class PricingService {
  private growthBook = inject(GROWTHBOOK);
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private state = inject(StateService);

  public pricingInfo$ = of(undefined).pipe(
    switchMap(() => {
      return this.http.get<SubscriptionPricingDto>(`${this.config.apiEndpoint}/billing/subscription/pricing`, {
        transferCache: false,
      });
    }),
    shareReplay(1)
  );

  public isDowngrade(subscription: SubscriptionDtoV2, to: Product) {
    if (!subscription || !to) return false;
    if (subscription.is_free_subscription === true) return false;
    const fromPrice = subscription.products.main.price;
    if (fromPrice.product_type === 'free') return false;
    const newMonthlyPrice = to.prices.find(p => p.recurring_interval === fromPrice.recurring_interval);
    if (!newMonthlyPrice) return true; // going to free
    if (newMonthlyPrice.product_type === 'free') return true;
    return newMonthlyPrice.currencies['usd'].unit_amount / 100 < fromPrice.currencies['usd'].unit_amount / 100;
  }

  public isUpgrade(subscription: SubscriptionDtoV2, to: Product) {
    return !this.isDowngrade(subscription, to);
  }

  public getTrialLengthDays() {
    return this.growthBook.getFeatureValue('trial-length', 14);
  }
  public getTrialExtensionDays() {
    return this.growthBook.getFeatureValue('trial-extension-length', 7);
  }

  public getPriceValue(price: PriceDtoV2, currency: string): number {
    if (!price) return;
    if (price.product_type === 'enterprise') return;
    if (price.product_type === 'free') return 0;
    if (!price || !currency) return;
    if (!price.currencies) {
      if (price.currencies['usd'].unit_amount === 0) return 0;
      console.warn('No currency options found for price:', price);
      return;
    }
    const currencyOption = price.currencies[currency.toLowerCase()];
    if (!currencyOption) {
      console.error(`Currency option not found for currency: ${currency}`, price.currencies, price);
      return;
    }
    return currencyOption.unit_amount;
  }

  getPriceDetails(
    prices: PriceDtoV2[],
    promo: PromotionWithCode,
    billingFrequency: RecurringInterval,
    currency: string
  ): PriceDetailsCalculation {
    const monthlyPrice = prices.find(price => price.recurring_interval === 'month');
    const yearlyPrice = prices.find(price => price.recurring_interval === 'year');

    const monthlyPriceValue = this.getPriceValue(monthlyPrice, currency);
    const yearlyPriceValue = this.getPriceValue(yearlyPrice, currency);

    let monthlyDiscountAmount = 0;
    let yearlyDiscountAmount = 0;
    if (promo?.percent_off) {
      if (monthlyPriceValue) {
        monthlyDiscountAmount = (monthlyPriceValue / 100 / 100) * promo.percent_off;
      }
      if (yearlyPriceValue) {
        yearlyDiscountAmount = (yearlyPriceValue / 100 / 100) * promo.percent_off;
      }
    }
    // Not showing fixed discount amounts here on purpose.

    const monthlyAmountWithoutDiscount = monthlyPriceValue ? monthlyPriceValue / 100 : 0;
    const yearlyAmountWithoutDiscount = yearlyPriceValue ? yearlyPriceValue / 100 : 0;
    const monthlyAmount = monthlyPriceValue ? monthlyPriceValue / 100 - monthlyDiscountAmount : 0;
    const yearlyAmount = yearlyPriceValue ? yearlyPriceValue / 100 - yearlyDiscountAmount : 0;

    return {
      monthlyAmount,
      monthlyAmountWithoutDiscount,
      yearlyAmount,
      yearlyAmountWithoutDiscount,
      monthlyDiscountAmount,
      displayAmount: billingFrequency === 'year' ? yearlyAmount / 12 : monthlyAmount,
      currency,
    };
  }

  public getDefaultPrice(billingFrequency: RecurringInterval, pricesForCorrectType: PriceDtoV2[] = []) {
    return pricesForCorrectType.find(price => price.recurring_interval === billingFrequency);
  }

  public canStartTrial(subscription: SubscriptionDtoV2) {
    if (!subscription) return false;
    const trialLength = this.getTrialLengthDays();
    if (!trialLength) return false;
    if (!subscription.is_free_subscription) return false;
    return this.commonTrialCheck();
  }

  public canExtendTrial(subscription: SubscriptionDtoV2) {
    if (!subscription) return false;
    if (subscription.status !== 'trialing') return false;
    const trialExtensionDays = this.getTrialExtensionDays();
    if (!trialExtensionDays) return false;
    return this.commonTrialCheck();
  }

  private commonTrialCheck() {
    const tenant = this.state.activeTenant$?.value;
    if (!tenant) return false;
    if (!tenant.trial_modified_at) return true;
    const timeSinceTrialModified = new Date().getTime() - tenant.trial_modified_at * 1000;
    const daysSinceTrialModified = Math.floor(timeSinceTrialModified / 1000 / 60 / 60 / 24);
    return daysSinceTrialModified > minTimeBetweenTrialsInDays;
  }
}
